/**
 * CharacterDataService - Complete D&D 5e Character Data Management
 * 
 * This service loads and manages complete D&D 5e character sheets with:
 * - Full spell lists and spellcasting abilities
 * - Class features and racial abilities  
 * - Complete equipment and proficiencies
 * - Proper action lists for combat integration
 * 
 * @date_of_change 2025-08-30
 * - Initial implementation to replace basic character templates with full sheets
 */

// Import character data files
import wizardData from '../data/characters/wizard.json';
import clericData from '../data/characters/cleric.json';
import fighterData from '../data/characters/fighter.json';
import paladinData from '../data/characters/paladin.json';
import rogueData from '../data/characters/rogue.json';
import rangerData from '../data/characters/ranger.json';
import barbarianData from '../data/characters/barbarian.json';
import { DEBUG_ENABLED } from '../utils/DebugLogger';

/**
 * Character Data Registry
 */
const CHARACTER_DATA = {
  wizard: wizardData,
  cleric: clericData, 
  fighter: fighterData,
  paladin: paladinData,
  rogue: rogueData,
  ranger: rangerData,
  barbarian: barbarianData
};

/**
 * Character Templates for UI Display
 */
export const characterTemplates = {
  wizard: {
    id: 'wizard',
    name: 'Wizard',
    description: 'A studious spellcaster who learns magic through research and practice',
    primaryClass: 'Wizard',
    level: 1,
    imageUrl: '/assets/characters/wizard.png',
    tags: ['spellcaster', 'intelligence', 'arcane']
  },
  cleric: {
    id: 'cleric',
    name: 'Cleric', 
    description: 'A divine spellcaster who channels the power of their deity',
    primaryClass: 'Cleric',
    level: 1,
    imageUrl: '/assets/characters/cleric.png',
    tags: ['spellcaster', 'wisdom', 'divine', 'healer']
  },
  fighter: {
    id: 'fighter',
    name: 'Fighter',
    description: 'A master of martial combat skilled with a variety of weapons and armor',
    primaryClass: 'Fighter', 
    level: 1,
    imageUrl: '/assets/characters/fighter.png',
    tags: ['martial', 'strength', 'warrior']
  },
  paladin: {
    id: 'paladin',
    name: 'Paladin',
    description: 'A holy warrior who fights with divine magic and righteous conviction',
    primaryClass: 'Paladin',
    level: 1,
    imageUrl: '/assets/characters/paladin.png',
    tags: ['martial', 'divine', 'charisma', 'tank']
  },
  rogue: {
    id: 'rogue',
    name: 'Rogue',
    description: 'A skilled infiltrator who strikes from the shadows with precision',
    primaryClass: 'Rogue',
    level: 1,
    imageUrl: '/assets/characters/rogue.png',
    tags: ['stealth', 'dexterity', 'skills', 'damage']
  },
  ranger: {
    id: 'ranger',
    name: 'Ranger',
    description: 'A skilled hunter and tracker who protects the wilderness',
    primaryClass: 'Ranger',
    level: 1,
    imageUrl: '/assets/characters/ranger.png',
    tags: ['nature', 'tracking', 'ranged', 'survival']
  },
  barbarian: {
    id: 'barbarian',
    name: 'Barbarian',
    description: 'A fierce warrior who enters battle in a primal rage',
    primaryClass: 'Barbarian',
    level: 1,
    imageUrl: '/assets/characters/barbarian.png',
    tags: ['rage', 'strength', 'damage', 'tank']
  }
};

/**
 * Character Data Service
 */
export class CharacterDataService {
  static debugLog(...args) {
    if (DEBUG_ENABLED) {
      console.debug(...args);
    }
  }
  
  /**
   * Get a character template for UI display
   */
  static getCharacterTemplate(templateName) {
    return characterTemplates[templateName] || null;
  }

  /**
   * Get all available character templates
   */
  static getAllCharacterTemplates() {
    return Object.keys(characterTemplates).map(key => ({
      key,
      ...characterTemplates[key]
    }));
  }

  /**
   * Create a complete character from template
   */
  static createCharacterFromTemplate(templateName, customizations = {}) {
    this.debugLog(`[CharacterDataService] Creating character from template: ${templateName}`);

    const characterData = CHARACTER_DATA[templateName];
    if (!characterData) {
      throw new Error(`Character template '${templateName}' not found`);
    }

    // Deep clone the character data to avoid mutations
    const character = JSON.parse(JSON.stringify(characterData));
    
    // Apply customizations
    if (customizations.name) {
      character.name = customizations.name;
    }
    
    // Generate unique ID if not provided
    if (!character.id || customizations.generateId) {
      character.id = `${templateName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Update metadata
    character.metadata = {
      ...character.metadata,
      created: new Date().toISOString(),
      modified: new Date().toISOString()
    };

    this.debugLog(`[CharacterDataService] Created character:`, {
      id: character.id,
      name: character.name,
      class: character.class,
      level: character.level,
      spells: character.spells ? Object.keys(character.spells).length : 0,
      actions: character.actions ? character.actions.length : 0
    });

    return character;
  }

  /**
   * Get character actions for combat system
   */
  static getCharacterActions(character) {
    if (!character || !character.actions) {
      console.warn('[CharacterDataService] No actions found for character:', character?.name);
      return [];
    }

    return character.actions.map(action => ({
      ...action,
      // Ensure compatibility with ActionTracker
      bonus: action.attackBonus,
      damage: action.damage,
      desc: action.description,
      isCharacterAction: true,
      originalAction: action
    }));
  }

  /**
   * Get character spells for spell tracking
   */
  static getCharacterSpells(character) {
    if (!character || !character.spells) {
      return { cantrips: [], level1: [], level2: [], level3: [] };
    }

    return character.spells;
  }

  /**
   * Get character spell slots
   */
  static getCharacterSpellSlots(character) {
    if (!character?.data?.spellcasting?.slots) {
      return {};
    }
    return character.data.spellcasting.slots;
  }

  static convertToTokenFormat(character, additionalProps = {}) {
    const TokenAdapter = require('./TokenAdapter').default;
    const core = TokenAdapter.toTokenCore(character);
    return {
      id: character.id,
      name: core.name,
      type: 'player',
      level: character?.data?.classes?.[0]?.level || 1,
      hp: core.hp,
      maxHp: core.maxHp,
      ac: core.ac,
      speed: core.speed ?? 30,
      initiative: core.initiative ?? null,
      hpObj: core.hpObj,
      abilities: core.abilities,
      stats: core.abilitiesShort,
      savingThrows: character?.data?.savingThrows,
      skills: character?.data?.skills,
      actions: character.actions || [],
      spellcasting: character?.data?.spellcasting,
      spells: character.spells,
      equipment: character?.data?.equipment,
      weapons: character?.data?.equipment?.weapons || [],
      armor: character?.data?.equipment?.armor || [],
      features: character?.data?.features || [],
      feats: character?.data?.feats || [],
      conditions: character?.data?.conditions || [],
      characterData: character,
      imageUrl: character?.data?.imageUrl,
      ...additionalProps
    };
  }

  /**
   * Update character HP after combat
   */
  static updateCharacterHP(character, newCurrentHP) {
    const updated = JSON.parse(JSON.stringify(character));
    updated.data.hitPoints.current = Math.max(0, Math.min(newCurrentHP, updated.data.hitPoints.maximum));
    updated.metadata.modified = new Date().toISOString();
    return updated;
  }

  /**
   * Get character's spell attack bonus
   */
  static getSpellAttackBonus(character) {
    if (!character?.data?.spellcasting) return 0;
    
    const spellcastingAbility = character.data.spellcasting.ability;
    const abilityScore = character.data.abilities[spellcastingAbility];
    const modifier = Math.floor((abilityScore - 10) / 2);
    const proficiency = character.data.combatStats.proficiencyBonus;
    
    return modifier + proficiency;
  }

  /**
   * Get character's spell save DC
   */
  static getSpellSaveDC(character) {
    return 8 + this.getSpellAttackBonus(character);
  }

  /**
   * Check if character can cast spell
   */
  static canCastSpell(character, spellLevel) {
    const slots = this.getCharacterSpellSlots(character);
    const levelSlots = slots[spellLevel];
    
    return levelSlots && (levelSlots.total - levelSlots.used) > 0;
  }

  /**
   * Use spell slot
   */
  static useSpellSlot(character, spellLevel) {
    const updated = JSON.parse(JSON.stringify(character));
    const slots = updated.data.spellcasting.slots;
    
    if (slots[spellLevel] && slots[spellLevel].used < slots[spellLevel].total) {
      slots[spellLevel].used += 1;
      updated.metadata.modified = new Date().toISOString();
      return updated;
    }
    
    return character; // No change if no slots available
  }

  /**
   * Get character class features 
   */
  static getCharacterFeatures(character) {
    return character?.data?.features || [];
  }

  /**
   * Get character proficiencies
   */
  static getCharacterProficiencies(character) {
    return character?.data?.proficiencies || {};
  }

  /**
   * Get raw character data by ID
   * @date_of_change 2025-09-06 - Added for EntityStorage integration
   */
  static getCharacterData(characterId) {
    this.debugLog(`[CharacterDataService] Looking for character: ${characterId}`);
    this.debugLog(`[CharacterDataService] Available characters:`, Object.keys(CHARACTER_DATA));
    this.debugLog(`[CharacterDataService] CHARACTER_DATA sample:`, {
      wizard: !!CHARACTER_DATA.wizard,
      wizardType: typeof CHARACTER_DATA.wizard,
      wizardKeys: CHARACTER_DATA.wizard ? Object.keys(CHARACTER_DATA.wizard) : 'undefined'
    });
    
    const data = CHARACTER_DATA[characterId];
    if (!data) {
      console.warn(`[CharacterDataService] ❌ No character data found for: ${characterId}`);
      return null;
    }
    
    this.debugLog(`[CharacterDataService] Found data for ${characterId}:`, data);
    return data;
  }
}

export default CharacterDataService;
